import { useEffect, useState, useCallback, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { formatDate } from "../../Utilities/dateUtils";
import { GetLicenseManagementCustomers } from "../../actions/superUserQueries";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Button, Input, UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";
import { CreateNewScormLicenseLicenses } from "../../actions/superUserMutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WarnLimitForLicMgt } from "../../constants/ConstantVars";
import DataGrid from "../DataGrids/DataGrid";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

function LicMgtLicensesTable({
  customer,
  toggleUpdate,
  handleUpdateData,
}) {
  const intl = useIntl();
  const [licenseRows, setLicenseRows] = useState([]);

  const itemsRef = useRef([]);
  const { mutate } = useMutation({ mutationFn: CreateNewScormLicenseLicenses });

  const queryClient = useQueryClient();
  const licenseCustomers = GetLicenseManagementCustomers();

  const handleAddLicenseForCourse = useCallback(
    (courseId, i) => {
      if (
        typeof itemsRef.current[i] === "object" ||
        itemsRef.current[i].length < 1
      ) {
        toast.warn(intl.formatMessage({ id: "YouMustFillInNumberOfLicenses" }));
        return;
      }

      const quantity = Number(itemsRef.current[i]);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
        toast.warn(intl.formatMessage({ id: "InvalidNumberOfLicenses" }));
        return;
      }

      mutate(
        {
          courseId: courseId,
          customerId: customer.customer.id,
          quantity: quantity,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              toast.success(<FormattedMessage id="LicenseAdded" />);
              let tempCustomerData = [...licenseCustomers.data];
              let itemIndex = tempCustomerData.findIndex(
                (cust) => cust.customer.id === customer.customer.id
              );
              tempCustomerData[itemIndex].licensesPerCourse =
                data.data.result.licensesPerCourse;
              queryClient.setQueryData(
                ["FetchLicenseManagementCustomers"],
                tempCustomerData
              );
              handleUpdateData();
            } else {
              let errorMessage = <FormattedMessage id="GenericApiError" />;
              if (data?.response?.data) {
                errorMessage = formatServiceError(intl, data.response.data);
              }
              toast.warn(errorMessage);
            }
          },
          throwOnError: true,
          onError: (err) => {
            toast.warn(err);
          },
        }
      );
    },
    [
      customer.customer.id,
      licenseCustomers.data,
      handleUpdateData,
      queryClient,
      mutate,
      intl,
    ]
  );

  const addLicensesCol = useCallback(
    (courseId, i) => {
      return (
        <>
          <Input
            type="number"
            min={1}
            max={100}
            step={1}
            placeholder={intl.formatMessage({ id: "NumberOfLicenses" })}
            ref={(el) => (itemsRef.current[i] = el)}
            onChange={(e) => {
              itemsRef.current[i] = e.target.value;
            }}
          />
          <Button
            outline
            color="primary"
            onClick={() => handleAddLicenseForCourse(courseId, i)}
          >
            <FormattedMessage id="AddLicenses" />
          </Button>
        </>
      );
    },
    [handleAddLicenseForCourse, intl]
  );

  const getStatusIcon = useCallback(
    (nbrOfLicensesNotUsed, courseId) => {
      let icon = "thumbs-up";
      let colorClass = "text-success";
      let tooltipText = intl.formatMessage({ id: "EnoughUnusedLicenses" });

      if (nbrOfLicensesNotUsed === 0) {
        icon = "triangle-exclamation";
        colorClass = "text-danger";
        tooltipText = intl.formatMessage({ id: "NoUnusedLicensesLeft" });
      } else if (nbrOfLicensesNotUsed < WarnLimitForLicMgt + 1) {
        icon = "triangle-exclamation";
        colorClass = "text-warning";
        tooltipText = intl.formatMessage(
          { id: "FewUnusedLicensesLeft" },
          { limit: WarnLimitForLicMgt },
        );
      }

      const tooltipId = `licenseStatusTooltip-${courseId}`;

      return (
        <>
          <FontAwesomeIcon id={tooltipId} icon={icon} className={colorClass} />
          <UncontrolledTooltip target={tooltipId} fade={false}>
            {tooltipText}
          </UncontrolledTooltip>
        </>
      );
    },
    [intl],
  );

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(
      0,
      licenseCustomers.data.find(
        (liccust) => liccust.customer.id === customer.customer.id
      ).licensesPerCourse.length
    );
    const licenseManagementTableDataSource = [];
    licenseCustomers.data
      .find((liccust) => liccust.customer.id === customer.customer.id)
      .licensesPerCourse.forEach((lic, i) => {
        let student = {
          uniqueId: lic.course.id,
          name: lic.course.name,
          nbrOfLicenses: lic.nbrOfLicenses,
          nbrOfValidLicenses: lic.nbrOfValidLicenses,
          nbrOfLicensesNotUsed: lic.nbrOfLicensesNotUsed,
          created: formatDate(lic.course.created, "P"),
          administer: addLicensesCol(lic.course.id, i),
          status: getStatusIcon(lic.nbrOfLicensesNotUsed, lic.course.id),
        };
        licenseManagementTableDataSource.push(student);
      });
    setLicenseRows(licenseManagementTableDataSource);
  }, [
    licenseCustomers.data,
    toggleUpdate,
    customer.customer.id,
    addLicensesCol,
    getStatusIcon,
  ]);

  const columns = [
    { id: "name", key: "name", header: <FormattedMessage id="Name" />, width: 150 },
    { id: "nbrOfLicenses", key: "nbrOfLicenses", header: <FormattedMessage id="NumberOfLicenses" />, width: 170 },
    { id: "nbrOfValidLicenses", key: "nbrOfValidLicenses", header: <FormattedMessage id="NumberOfValidLicenses" />, width: 200 },
    { id: "nbrOfLicensesNotUsed", key: "nbrOfLicensesNotUsed", header: <FormattedMessage id="NumberOfUnusedLicenses" />, width: 220 },
    { id: "created", key: "created", header: <FormattedMessage id="Created" />, width: 120 },
    { id: "administer", key: "administer", header: <FormattedMessage id="Administer" />, width: 150, sortable: false, cell: (props) => props.getValue() },
    { id: "status", key: "status", header: <FormattedMessage id="Status" />, width: 90, sortable: false, cell: (props) => props.getValue() },
  ];

  return (
    <DataGrid
      rows={licenseRows}
      columns={columns}
      getRowId={(row) => row.uniqueId}
      noDataMessage={<FormattedMessage id="NoLicenses" />}
    />
  );
}

export default LicMgtLicensesTable;
